import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FeedIcon from "@mui/icons-material/DynamicFeedOutlined";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import SyncIcon from "@mui/icons-material/Sync";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageContainer, PageHeader } from "../components/PageHeader";
import DangerConfirmDialog from "../components/DangerConfirmDialog";
import {
  AddSupplierDialog,
  EditFeedDialog,
} from "../components/contentFeeds/ContentFeedDialogs";
import { friendlyError } from "../utils/apiError";
import { describeError, useToast } from "../components/toast";
import { tokens } from "../theme";
import { foyerApi, type ContentSupplierRow } from "../http/foyer";
import { PermissionGate } from "../auth/access";
import {
  AUTHOR_SUPPLIERS,
  TRUST_SUPPLIERS,
} from "../components/distribution/catalogueGates";
import { useFormat } from "../i18n/format";

/** An empty cell: a symbol, not a word, so it is the same in every language. */
const NONE = "—";

/** Where a feed with a company is governed: that company's settings. */
export function companySettingsHref(companyId: string): string {
  return `/companies?settings=${encodeURIComponent(companyId)}`;
}

/**
 * Content providers — who Nenda has licensed, and who is TRUSTED.
 *
 * ## Why this is not a tab on Packages
 *
 * It used to sit on that page, above the packages themselves, and it made the
 * screen read as though a provider were a detail of a package. It is not. Four
 * separate subsystems read `ContentSupplier`:
 *
 *   - `entitlementResolver` — the play gate. Only a TRUSTED provider's content
 *     is carried by a rule, so this screen decides what a guest can watch.
 *   - `bulkIngestScan` — an upload is attributed to the provider the account is
 *     bound to, never to the one the metadata claims.
 *   - `ContentOwner` / `AssetOwner` — a provider's own login to the
 *     content-owner portal (its id IS this row's id — see below).
 *   - `contentPackages` — rules.
 *
 * Nesting it under Packages would encode a relationship that does not exist,
 * and packages are the LAST of the four to matter.
 *
 * ## Login is a capability of a provider, not a second organisation, and not
 * a second SCREEN either
 *
 * Ruled by Sigge 2026-09-04 (`[[mrss-feed-content-providers]]`): a content
 * provider can be catalogue-attribution-only (SVT, TV4 Media — no login,
 * ever), or it can also have a login to upload and read its own numbers
 * (Tigra). Either way it is ONE row, at ONE id — the real upstream
 * `provider.providerId`.
 *
 * The first cut of this ruling still rendered a whole second card below this
 * table listing every login as its own organisation — the same "two lists"
 * shape the ruling was supposed to remove, just moved down a level. Corrected
 * the same day: the **Login** column is a pure STATUS marker — an edit icon
 * when a login exists, nothing at all when it does not.
 *
 * A second cut then added a "give login" icon here too — and Sigge reversed
 * that the same day (`[[mrss-feed-content-providers]]`): a login with nobody
 * to use it is not a step forward, so the ONE place that creates one is the
 * Users page, as part of giving a real person access — see `ScopePicker` in
 * `UsersPage.tsx`. This page only ever shows a login that already has
 * somebody attached to it. And "which USER is tied to which provider" is
 * answered on the Users page too, in the Scope column — a question about a
 * login *account*, not about the provider, and belongs where accounts live.
 *
 * ## The MRSS feed is a fact about the PROVIDER, not the login
 *
 * First cut, same day: the feed lived on `ContentOwner`, so attaching one
 * required a login to exist first. Wrong within hours — DroneTV is a
 * feed-only aggregator (AirVuz/DroneTV) with nobody at the source ever
 * logging in, and it still needed a feed. Moved to `ContentSupplier` itself:
 * a login and a feed are two independent capabilities a provider may hold,
 * neither implying the other. Unlike Login, this column both shows AND
 * creates the feed right here — a feed needs nobody to use it, so there is
 * no Users-page-style reason to route its creation elsewhere.
 *
 * ## And the permissions differ, which settles it
 *
 * Seeing this screen is `canManageDistribution`. **Trusting a provider is a
 * second key, `canTrustSuppliers`, enforced on the route.** Half a page cannot
 * sensibly be gated on a different permission from the other half — so a tab
 * was never really available, only a tab-shaped nav item.
 *
 * The split is deliberate: seeing the catalogue is part of managing it;
 * committing Nenda to a supplier is a different act with a different blast
 * radius. A rule over a newly-trusted provider changes what plays in every room
 * that holds a package naming it.
 */
export default function ContentProvidersPage() {
  const { t } = useTranslation(["content", "common"]);
  const [suppliers, setSuppliers] = useState<ContentSupplierRow[]>([]);
  const [catalogueReachable, setCatalogueReachable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sups = await foyerApi.listContentSuppliers();
      setSuppliers(sups.suppliers);
      setCatalogueReachable(sups.catalogueReachable);
      setError(null);
    } catch (e) {
      setError(friendlyError(t, e));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const trustedCount = suppliers.filter((s) => s.trusted).length;

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("common:labels.distribution")}
        title={t("contentProvidersPage.title")}
        subtitle={t("contentProvidersPage.subtitle")}
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      {!catalogueReachable ? (
        // Our own answer is still in hand, so the screen works. Saying which
        // half is missing beats a spinner that never resolves.
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t("contentProvidersPage.catalogueUnreachable")}
        </Alert>
      ) : null}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <SuppliersCard
          suppliers={suppliers}
          trustedCount={trustedCount}
          onChanged={load}
        />
      )}
    </PageContainer>
  );
}

/* -------------------------------------------------------------------------- */

function SuppliersCard({
  suppliers,
  trustedCount,
  onChanged,
}: {
  suppliers: ContentSupplierRow[];
  trustedCount: number;
  onChanged: () => Promise<void>;
}) {
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const fmt = useFormat();
  const [busy, setBusy] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();
  const [editingFeed, setEditingFeed] = useState<ContentSupplierRow | null>(
    null
  );
  const [deleting, setDeleting] = useState<ContentSupplierRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const toggle = async (supplier: ContentSupplierRow) => {
    setBusy(supplier.id);
    setError(null);
    try {
      const res = await foyerApi.trustSupplier(supplier.id, !supplier.trusted);
      // Withdrawing trust silently empties every rule that named the supplier.
      // An operator who is not shown that finds out from a guest.
      const packages = res.affectedPackages.map((p) => p.name).join(", ");
      setWarning(
        res.affectedPackages.length > 0
          ? t("contentProvidersPage.packagesChanged", {
              name: supplier.name,
              count: res.affectedPackages.length,
              packages,
            })
          : null
      );
      const changed =
        res.affectedPackages.length > 0
          ? t("contentProvidersPage.packagesChangedSuffix", { packages })
          : "";
      toast.success(
        supplier.trusted
          ? t("contentProvidersPage.trustWithdrawn", {
              name: supplier.name,
              changed,
            })
          : t("contentProvidersPage.nowTrusted", {
              name: supplier.name,
              changed,
            })
      );
      await onChanged();
    } catch (e) {
      setError(friendlyError(t, e));
      toast.error(
        describeError(
          t,
          supplier.trusted ? "withdrawSupplierTrust" : "trustSupplier",
          e,
          { name: supplier.name }
        )
      );
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(deleting.id);
    setDeleteError(null);
    try {
      await foyerApi.deleteContentSupplier(deleting.id);
      setDeleting(null);
      toast.success(t("contentProvidersPage.deleted", { name: deleting.name }));
      await onChanged();
    } catch (e) {
      const err = e as { body?: { hint?: string }; message: string };
      setDeleteError(err.body?.hint ?? friendlyError(t, e));
      toast.error(
        describeError(t, "deleteContentFeed", e, { name: deleting.name })
      );
    } finally {
      setBusy(null);
    }
  };

  const sync = async () => {
    setBusy("__sync__");
    setError(null);
    try {
      const res = await foyerApi.syncContentSuppliers();
      toast.success(
        t("contentProvidersPage.synced", {
          synced: res.synced,
          created: res.created,
        })
      );
      await onChanged();
    } catch (e) {
      setError(friendlyError(t, e));
      toast.error(describeError(t, "syncContentFeeds", e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card variant="outlined" sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">{t("contentProvidersPage.title")}</Typography>
        {/* Never wraps: the buttons keep their width, the title gives way. */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexShrink: 0, "& .MuiButton-root": { whiteSpace: "nowrap" } }}
        >
          <PermissionGate {...TRUST_SUPPLIERS}>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAdding(true)}
              disabled={busy !== null}
              data-testid="content-supplier-add"
            >
              {t("contentProvidersPage.addFeed")}
            </Button>
          </PermissionGate>
          <PermissionGate {...AUTHOR_SUPPLIERS}>
            <Button
              size="small"
              startIcon={<SyncIcon />}
              onClick={() => void sync()}
              disabled={busy !== null}
            >
              {t("contentProvidersPage.sync")}
            </Button>
          </PermissionGate>
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {warning ? (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setWarning(null)}>
          {warning}
        </Alert>
      ) : null}

      {trustedCount === 0 ? (
        // The empty-estate case, and the one that matters most: with nothing
        // trusted, every rule anywhere matches nothing. Same lesson the ingest
        // picker learned the hard way (see ingest-entitlement-reachability).
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("contentProvidersPage.noneTrusted")}
        </Alert>
      ) : null}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("contentProvidersPage.columns.feed")}</TableCell>
              <TableCell>{t("common:labels.id")}</TableCell>
              <TableCell>{t("common:labels.company")}</TableCell>
              <TableCell>{t("contentProvidersPage.columns.mrss")}</TableCell>
              <TableCell>
                {t("contentProvidersPage.columns.trustedSince")}
              </TableCell>
              <TableCell align="right">
                {t("contentProvidersPage.columns.trusted")}
              </TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {suppliers.map((supplier) => (
              <TableRow key={supplier.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FeedIcon
                      fontSize="small"
                      sx={{ color: tokens.inkMuted }}
                      aria-hidden
                    />
                    <Typography variant="body2">{supplier.name}</Typography>
                    {!supplier.known ? (
                      <Tooltip
                        title={t("contentProvidersPage.notRuledTooltip")}
                      >
                        <Chip
                          size="small"
                          label={t("contentProvidersPage.notRuled")}
                        />
                      </Tooltip>
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "fontFamilyMono",
                      color: tokens.inkMuted,
                    }}
                  >
                    {supplier.id}
                  </Typography>
                </TableCell>
                {/* The company that governs this feed (Sigge, 2026-10-19):
                    company icon + name, linking to that company's settings,
                    where its MRSS, login and storefront are edited. Empty for
                    a catalogue-only feed. */}
                <TableCell>
                  {supplier.company ? (
                    <Tooltip
                      title={t("contentProvidersPage.governedFrom", {
                        company: supplier.company.name,
                      })}
                    >
                      <Button
                        size="small"
                        startIcon={<BusinessIcon fontSize="small" />}
                        onClick={() =>
                          navigate(companySettingsHref(supplier.company!.id))
                        }
                        sx={{ textTransform: "none" }}
                        data-testid={`supplier-company-${supplier.id}`}
                      >
                        {supplier.company.name}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {NONE}
                    </Typography>
                  )}
                </TableCell>
                {/* Type C (`[[mrss-feed-content-providers]]`): a feed this
                    PROVIDER delivers through — deliberately independent of
                    Login (`hasLogin`). Unlike a login, a feed needs nobody to
                    use it, so — unlike Login — this table both shows AND
                    creates it: filled icon to edit, muted icon to add. */}
                <TableCell>
                  <Tooltip
                    title={
                      supplier.company
                        ? t(
                            supplier.mrssFeedUrl
                              ? "contentProvidersPage.hasFeedInCompany"
                              : "contentProvidersPage.noFeedInCompany",
                            { company: supplier.company.name }
                          )
                        : supplier.mrssFeedUrl
                          ? t("contentProvidersPage.hasFeed")
                          : t("contentProvidersPage.noFeed")
                    }
                  >
                    <PermissionGate
                      {...AUTHOR_SUPPLIERS}
                      deniedProps={{ disabled: true }}
                    >
                      <IconButton
                        size="small"
                        disabled={busy !== null}
                        // Has one: the SAME brand blue as every other primary
                        // affordance — a feed is a normal, positive state here,
                        // not a "success" to celebrate. Has none: a light grey,
                        // deliberately fainter than the row's own text, so it
                        // reads as absent rather than as another action.
                        color={supplier.mrssFeedUrl ? "primary" : undefined}
                        sx={
                          supplier.mrssFeedUrl
                            ? undefined
                            : { color: tokens.inkFaint }
                        }
                        onClick={() =>
                          supplier.company
                            ? navigate(companySettingsHref(supplier.company.id))
                            : setEditingFeed(supplier)
                        }
                        aria-label={
                          supplier.company
                            ? t("contentProvidersPage.feedInCompanyAria", {
                                name: supplier.name,
                                company: supplier.company.name,
                              })
                            : supplier.mrssFeedUrl
                              ? t("contentProvidersPage.editFeedAria", {
                                  name: supplier.name,
                                })
                              : t("contentProvidersPage.addFeedAria", {
                                  name: supplier.name,
                                })
                        }
                        data-testid={`supplier-mrss-feed-${supplier.id}`}
                      >
                        <RssFeedIcon fontSize="small" />
                      </IconButton>
                    </PermissionGate>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {supplier.trustedAt
                      ? supplier.trustedBy
                        ? t("contentProvidersPage.trustedBy", {
                            date: fmt.date(supplier.trustedAt),
                            by: supplier.trustedBy,
                          })
                        : fmt.date(supplier.trustedAt)
                      : NONE}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <PermissionGate
                    {...TRUST_SUPPLIERS}
                    deniedProps={{ disabled: true }}
                  >
                    <Switch
                      checked={supplier.trusted}
                      disabled={busy !== null}
                      onChange={() => void toggle(supplier)}
                      inputProps={{
                        "aria-label": t("contentProvidersPage.trustAria", {
                          name: supplier.name,
                        }),
                      }}
                    />
                  </PermissionGate>
                </TableCell>
                <TableCell align="right">
                  <Tooltip
                    title={
                      supplier.trusted
                        ? t("contentProvidersPage.untrustFirst")
                        : supplier.company
                          ? t("contentProvidersPage.detachFirst", {
                              company: supplier.company.name,
                            })
                          : t("common:buttons.delete")
                    }
                  >
                    <span>
                      <PermissionGate
                        {...TRUST_SUPPLIERS}
                        deniedProps={{ disabled: true }}
                      >
                        <Button
                          size="small"
                          color="error"
                          disabled={
                            busy !== null ||
                            supplier.trusted ||
                            !!supplier.company
                          }
                          onClick={() => {
                            setDeleteError(null);
                            setDeleting(supplier);
                          }}
                          aria-label={t("contentProvidersPage.deleteAria", {
                            name: supplier.name,
                          })}
                          data-testid={`supplier-delete-${supplier.id}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </Button>
                      </PermissionGate>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {suppliers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    {t("contentProvidersPage.empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      {adding ? (
        <AddSupplierDialog
          onClose={() => setAdding(false)}
          onCreated={async () => {
            setAdding(false);
            await onChanged();
          }}
        />
      ) : null}

      {editingFeed ? (
        <EditFeedDialog
          supplier={editingFeed}
          onClose={() => setEditingFeed(null)}
          onSaved={async () => {
            setEditingFeed(null);
            await onChanged();
          }}
        />
      ) : null}

      <DangerConfirmDialog
        open={deleting !== null}
        title={t("contentProvidersPage.deleteTitle")}
        entityName={deleting?.name ?? ""}
        body={t("contentProvidersPage.deleteBody")}
        errorMessage={deleteError}
        busy={busy === deleting?.id}
        onConfirm={() => void remove()}
        onCancel={() => {
          setDeleting(null);
          setDeleteError(null);
        }}
      />
    </Card>
  );
}
